import React, { Component } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IStreamData } from "@formant/core/model/IStreamData";

import { scrollIntoView } from "../../../utils/scrollIntoView";
import { DeviceTags } from "../../common/DeviceTags";
import { AssetImage } from "../AssetImage";
import { ModuleFooter } from "../ModuleFooter";
import { flattenPoints } from "../utils/flattenPoints";
import { pointsKeys } from "../utils/pointsKeys";
import { ImageView } from "./ImageView";
import * as styles from "./TimeView.scss";

export interface ITimeViewProps {
    data: IStreamData<"image">[];
    time: Date;
    hovering?: boolean;
    live?: boolean;
    onSelectTime?: (time: Date) => void;
}

@observer
export class TimeView extends Component<ITimeViewProps> {
    private thumbnails?: HTMLElement;

    private ensureVisible = throttle(
        (_: HTMLElement) =>
            this.thumbnails && scrollIntoView(this.thumbnails, _),
        duration.second,
    );

    public render() {
        const { ensureVisible } = this;
        const { data, time, live, hovering, onSelectTime } = this.props;

        const points = flattenPoints(data)
            .slice()
            .sort(
                (a, b) =>
                    Math.abs(a.time.getTime() - time.getTime()) -
                    Math.abs(b.time.getTime() - time.getTime()),
            )
            .slice(0, 25)
            .sort((a, b) => a.time.getTime() - b.time.getTime());

        const selected =
            points
                .slice()
                .reverse()
                .find(
                    ({ time: pointTime }) =>
                        pointTime.getTime() <= time.getTime(),
                ) || points[0];

        const keys = pointsKeys(points);

        const thumbnails = points.map((point, i) => {
            const { time: pointTime, value } = point;
            const { url } = value;
            const onClick = () => onSelectTime && onSelectTime(pointTime);
            return (
                <div
                    key={keys[i]}
                    className={classnames(
                        styles.thumbnail,
                        point === selected && styles.selected,
                        point === selected && live && styles.live,
                    )}
                    onClick={onClick}
                    ref={
                        point === selected
                            ? _ => _ && ensureVisible(_)
                            : undefined
                    }
                >
                    <div className={styles.image}>
                        <AssetImage src={url} />
                    </div>
                </div>
            );
        });

        const selectedUrl = selected ? selected.value.url : undefined;
        const annotations = selected ? selected.value.annotations : undefined;

        return (
            <div className={styles["time-view"]}>
                <div className={styles.main}>
                    <div className={styles.view}>
                        <ImageView
                            src={selectedUrl}
                            annotations={annotations}
                        />
                    </div>
                </div>
                <ModuleFooter visible={hovering} floating={true}>
                    <div
                        className={styles.thumbnails}
                        ref={_ => (this.thumbnails = _ || undefined)}
                    >
                        {thumbnails}
                    </div>
                    {selected && (
                        <DeviceTags
                            deviceId={selected.deviceId}
                            time={selected.time}
                            tags={selected.tags}
                        />
                    )}
                </ModuleFooter>
            </div>
        );
    }
}
